/**
 * WorkflowSidebar
 *
 * Left sidebar showing workflow step navigation with status badges.
 * Displays progress metrics and allows navigation to any visited/completed step.
 *
 * Status Badges:
 * - READY (green solid) = Complete
 * - REVIEW (amber diamond) = Needs user confirmation
 * - BLOCKED (red circle) = Cannot proceed
 * - PENDING (gray outline) = Not started
 * - IN_PROGRESS (blue fill) = Currently active
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import {
  WORKFLOW_STEPS,
  type WorkflowStepId,
  type WorkflowStepStatus,
  type WorkflowProgress,
} from '@/types/workflow';
import { cn } from '@/lib/utils';
import {
  Check,
  ChevronRight,
  Circle,
  AlertTriangle,
  XCircle,
  Clock,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface WorkflowSidebarProps {
  currentStepId: WorkflowStepId;
  stepStatus: Record<WorkflowStepId, WorkflowStepStatus>;
  progress: WorkflowProgress;
  onStepClick: (stepId: WorkflowStepId) => void;
  blockerCount?: number;
  warningCount?: number;
  className?: string;
}

// Status icon and color mappings
const statusConfig: Record<
  WorkflowStepStatus,
  {
    icon: React.ReactNode;
    bgColor: string;
    textColor: string;
    borderColor: string;
  }
> = {
  ready: {
    icon: <Check className="h-3.5 w-3.5" />,
    bgColor: 'bg-green-500',
    textColor: 'text-white',
    borderColor: 'border-green-500',
  },
  review: {
    icon: <AlertTriangle className="h-3.5 w-3.5" />,
    bgColor: 'bg-amber-500',
    textColor: 'text-white',
    borderColor: 'border-amber-500',
  },
  blocked: {
    icon: <XCircle className="h-3.5 w-3.5" />,
    bgColor: 'bg-red-500',
    textColor: 'text-white',
    borderColor: 'border-red-500',
  },
  pending: {
    icon: null,
    bgColor: 'bg-transparent',
    textColor: 'text-muted-foreground',
    borderColor: 'border-muted-foreground/40',
  },
  in_progress: {
    icon: <Clock className="h-3.5 w-3.5" />,
    bgColor: 'bg-primary',
    textColor: 'text-primary-foreground',
    borderColor: 'border-primary',
  },
};

export function WorkflowSidebar({
  currentStepId,
  stepStatus,
  progress,
  onStepClick,
  blockerCount = 0,
  warningCount = 0,
  className,
}: WorkflowSidebarProps) {
  // Determine if a step can be clicked
  const canClickStep = (stepId: WorkflowStepId): boolean => {
    const status = stepStatus[stepId] || 'pending';
    // Can always click current, completed, or review steps
    // Can click any step that's not pending (for now, allow all navigation)
    return true; // Allow free navigation for better UX
  };

  return (
    <aside className={cn("hidden md:flex flex-col w-64 border-r bg-muted/30", className)}>
      {/* Steps Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 px-2">
          Steps
        </h3>
        <div className="space-y-1">
          {WORKFLOW_STEPS.map((step) => {
            const isCurrent = step.id === currentStepId;
            const status = isCurrent
              ? 'in_progress'
              : stepStatus[step.id] || 'pending';
            const config = statusConfig[status];
            const canClick = canClickStep(step.id);

            // Get issues for this step
            const stepIssues = {
              blockers: status === 'blocked' ? blockerCount : 0,
              warnings: status === 'review' ? warningCount : 0,
            };

            return (
              <TooltipProvider key={step.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => canClick && onStepClick(step.id)}
                      disabled={!canClick}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200',
                        isCurrent && 'bg-primary text-primary-foreground shadow-sm scale-[1.02]',
                        !isCurrent && canClick && 'hover:bg-muted hover:scale-[1.01] cursor-pointer',
                        !isCurrent && !canClick && 'opacity-50 cursor-not-allowed',
                        status === 'ready' && !isCurrent && 'bg-green-50 dark:bg-green-950/20'
                      )}
                    >
                      {/* Step indicator */}
                      <div
                        className={cn(
                          'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all duration-300',
                          isCurrent
                            ? 'bg-primary-foreground text-primary border-primary-foreground scale-110'
                            : cn(config.bgColor, config.textColor, config.borderColor),
                          status === 'ready' && !isCurrent && 'ring-2 ring-green-200 ring-offset-1'
                        )}
                      >
                        {config.icon || step.number}
                      </div>

                      {/* Step label */}
                      <div className="flex-1 min-w-0">
                        <span
                          className={cn(
                            'block text-sm font-medium truncate',
                            !isCurrent &&
                              status === 'pending' &&
                              'text-muted-foreground'
                          )}
                        >
                          {step.shortTitle}
                        </span>
                        {/* Show issue badges */}
                        {(stepIssues.blockers > 0 || stepIssues.warnings > 0) && (
                          <div className="flex gap-1 mt-0.5">
                            {stepIssues.blockers > 0 && (
                              <Badge
                                variant="destructive"
                                className="text-micro px-1 py-0 h-4"
                              >
                                {stepIssues.blockers}
                              </Badge>
                            )}
                            {stepIssues.warnings > 0 && (
                              <Badge
                                variant="outline"
                                className="text-micro px-1 py-0 h-4 border-amber-500 text-amber-600"
                              >
                                {stepIssues.warnings}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Current indicator */}
                      {isCurrent && (
                        <ChevronRight className="h-4 w-4 flex-shrink-0" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[200px]">
                    <p className="font-medium">{step.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {step.description}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </nav>

      {/* Progress Section */}
      <div className="border-t p-4 bg-background/50">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Progress
        </h4>
        <div className="space-y-3">
          {/* Data Completeness */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Data</span>
              <span className={cn(
                "font-medium transition-all duration-300",
                progress.dataCompletenessPct === 100 && "text-green-600"
              )}>
                {progress.dataCompletenessPct}%
                {progress.dataCompletenessPct === 100 && " ✓"}
              </span>
            </div>
            <Progress
              value={progress.dataCompletenessPct}
              className={cn(
                "h-1.5 [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out",
                progress.dataCompletenessPct === 100 && "[&>div]:bg-green-500"
              )}
            />
          </div>

          {/* Packet Completeness */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Documents</span>
              <span className={cn(
                "font-medium transition-all duration-300",
                progress.packetCompletenessPct === 100 && "text-green-600"
              )}>
                {progress.packetCompletenessPct}%
                {progress.packetCompletenessPct === 100 && " ✓"}
              </span>
            </div>
            <Progress
              value={progress.packetCompletenessPct}
              className={cn(
                "h-1.5 [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out",
                progress.packetCompletenessPct === 100 && "[&>div]:bg-green-500"
              )}
            />
          </div>

          {/* Compliance Readiness */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Ready</span>
              <span className={cn(
                "font-medium transition-all duration-300",
                progress.complianceReadinessPct === 100 && "text-green-600"
              )}>
                {progress.complianceReadinessPct}%
                {progress.complianceReadinessPct === 100 && " ✓"}
              </span>
            </div>
            <Progress
              value={progress.complianceReadinessPct}
              className={cn(
                "h-1.5 [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out",
                progress.complianceReadinessPct === 100 && "[&>div]:bg-green-500"
              )}
            />
          </div>
        </div>

        {/* Issue Summary */}
        {(blockerCount > 0 || warningCount > 0) && (
          <div className="mt-4 pt-3 border-t">
            <div className="flex items-center gap-2 flex-wrap">
              {blockerCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {blockerCount} {blockerCount === 1 ? 'Blocker' : 'Blockers'}
                </Badge>
              )}
              {warningCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-xs border-amber-500 text-amber-600"
                >
                  {warningCount} {warningCount === 1 ? 'Warning' : 'Warnings'}
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
